import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Center, Flex, HStack, Image, Input, Link, Spinner, Text, useDisclosure, VStack } from '@bonusly/design-system';

import type { GifPickerCard, GifPickerPill } from '../../../../lib/bizy/types/ui-blocks';
import { useBizyChatActions, useBizyUiBlockActionDispatcher } from '../context/actions-context';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { useBizyGifSearch, type BizyGif } from '../context/gif-context';
import { ChevronUpOutline } from '../lib/icons';
import { useDebounceValue } from '../lib/use-debounce-value';
import type { BizyNodeProps } from './node-props';

const MAX_RESULTS_HEIGHT = 320;

// Even/odd split into two masonry-ish columns (replaces the app's `split` util).
function splitColumns(gifs: BizyGif[]): [BizyGif[], BizyGif[]] {
	const left: BizyGif[] = [];
	const right: BizyGif[] = [];
	gifs.forEach((gif, i) => (i % 2 === 0 ? left : right).push(gif));
	return [left, right];
}

/**
 * Standalone `gif_picker_card` node — shown on demand when the user clicks
 * "Add a GIF" in the recognition draft card's wand menu (backend
 * `show_gif_picker`). It is self-contained: the node carries the recognition
 * `draft`, so picking a gif dispatches `attach_gif` (which re-emits the draft
 * card with the gif). Browse-only if no draft is carried or in read-only views.
 */
export function GifPickerCardNode({ node }: BizyNodeProps) {
	const block = node as GifPickerCard;
	const dispatch = useBizyUiBlockActionDispatcher();
	const { isReadOnly } = useBizyChatActions();
	const [attachPending, setAttachPending] = useState(false);
	const draft = block.payload.draft ?? null;

	// Selecting a gif is a one-shot: it dispatches attach_gif and then stays
	// disabled for this (historical) picker node. On success the backend
	// re-emits the draft card below, superseding it; to add a different gif the
	// user re-opens "Add a GIF" on the draft card, which emits a fresh picker.
	// We deliberately don't re-enable on a timer — that could double-dispatch a
	// slow attach_gif and duplicate the chat turn.
	const handleSelectGif = (gif: BizyGif) => {
		if (attachPending || !draft) return;
		setAttachPending(true);
		dispatch({ type: 'attach_gif', payload: { ...draft, attachment: gif.attachment } });
	};

	return (
		<GifPicker
			pills={block.payload.pills}
			primaryQuery={block.payload.primaryQuery}
			onSelectGif={draft && !isReadOnly && !attachPending ? handleSelectGif : undefined}
			isReadOnly={isReadOnly}
		/>
	);
}

/**
 * Collapsed "Attach Gif" disclosure. Expanding reveals contextual category
 * pills + a search box; results stream from the injected {@link useBizyGifSearch}
 * in a two-column layout. Presentational: picking a gif calls `onSelectGif`.
 */
export function GifPicker({
	pills,
	primaryQuery,
	onSelectGif,
	isReadOnly = false,
}: {
	pills: GifPickerPill[];
	primaryQuery?: string | null;
	onSelectGif?: (gif: BizyGif) => void;
	isReadOnly?: boolean;
}) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useBizyAnalytics();
	const searchGifs = useBizyGifSearch();
	const disclosure = useDisclosure({ defaultIsOpen: true });
	const containerRef = useRef<HTMLDivElement>(null);
	const initialTrackRef = useRef(track);

	const [inputValue, setInputValue] = useState(primaryQuery ?? pills[0]?.query ?? '');
	const query = useDebounceValue(inputValue, 300);

	const [gifs, setGifs] = useState<BizyGif[]>([]);
	const [provider, setProvider] = useState<{ name?: string; url?: string }>({});
	const [isLoading, setIsLoading] = useState(false);

	const canSelect = !isReadOnly && !!onSelectGif;

	useEffect(() => {
		initialTrackRef.current(BizyAnalyticsEvent.GifPickerOpened, { path: window.location.pathname });
	}, []);

	// Fetch while the picker is open; re-fetch on debounced query change.
	useEffect(() => {
		if (!disclosure.isOpen) return;
		let cancelled = false;
		setIsLoading(true);
		searchGifs(query)
			.then((result) => {
				if (cancelled) return;
				setGifs(result.gifs);
				setProvider({ name: result.providerName, url: result.providerUrl });
			})
			.catch(() => {
				if (!cancelled) setGifs([]);
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [disclosure.isOpen, query, searchGifs]);

	const [left, right] = splitColumns(gifs);

	const handleToggle = () => {
		const willOpen = !disclosure.isOpen;
		if (willOpen) {
			track(BizyAnalyticsEvent.GifPickerOpened, { path: window.location.pathname });
		}
		disclosure.onToggle();
		if (willOpen) {
			requestAnimationFrame(() => containerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }));
		}
	};

	const handlePill = (pillQuery: string) => {
		setInputValue(pillQuery);
		track(BizyAnalyticsEvent.GifPillClicked, { query: pillQuery, path: window.location.pathname });
	};

	const handleSelect = (gif: BizyGif) => {
		if (!canSelect) return;
		onSelectGif?.(gif);
		track(BizyAnalyticsEvent.GifSelected, { path: window.location.pathname });
	};

	return (
		<Box ref={containerRef} mt={2} border="1px solid" borderColor="border.default" borderRadius="md" bg="bg.surface" overflow="hidden">
			<Flex as="button" type="button" w="100%" align="center" justify="space-between" px={3} py={2} onClick={handleToggle} aria-expanded={disclosure.isOpen}>
				<Text fontSize="sm" fontWeight="semibold">
					{t('uiBlocks.gifPicker.title')}
				</Text>
				<ChevronUpOutline transitionProperty="transform" transitionDuration="fast" transform={disclosure.isOpen ? 'none' : 'rotate(180deg)'} />
			</Flex>

			{disclosure.isOpen && (
				<Box px={3} pb={3} role="group">
					<Input
						size="sm"
						mb={2}
						type="text"
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						placeholder={t('uiBlocks.gifPicker.searchPlaceholder', { provider: provider.name ?? '' })}
					/>

					{pills.length > 0 && (
						<HStack spacing={2} mb={2} overflowX="auto" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
							{pills.map((pill, i) => (
								<Button key={`${pill.label}-${i}`} size="xs" colorScheme="secondary" variant="ghost" flexShrink={0} onClick={() => handlePill(pill.query)}>
									{pill.label}
								</Button>
							))}
						</HStack>
					)}

					{isLoading ? (
						<Center h={`${MAX_RESULTS_HEIGHT}px`}>
							<Spinner />
						</Center>
					) : (
						<Flex gap={1} align="start" h={`${MAX_RESULTS_HEIGHT}px`} overflowY="auto" role="listbox">
							<GifColumn gifs={left} canSelect={canSelect} onSelect={handleSelect} />
							<GifColumn gifs={right} canSelect={canSelect} onSelect={handleSelect} />
						</Flex>
					)}

					{provider.name && (
						<Text fontSize="xs" color="text.softer" mt={2} textAlign="right">
							{t('uiBlocks.gifPicker.poweredBy')}{' '}
							<Link href={provider.url} isExternal fontWeight="semibold">
								{provider.name}
							</Link>
						</Text>
					)}
				</Box>
			)}
		</Box>
	);
}

function GifColumn({ gifs, canSelect, onSelect }: { gifs: BizyGif[]; canSelect: boolean; onSelect: (gif: BizyGif) => void }) {
	return (
		<VStack spacing={1} flex={1} minW={0}>
			{gifs.map((gif) => (
				<Box
					as="button"
					type="button"
					key={gif.id}
					role="option"
					w="100%"
					borderRadius="md"
					overflow="hidden"
					cursor={canSelect ? 'pointer' : 'default'}
					onClick={() => onSelect(gif)}
				>
					<Image alt={gif.description ?? ''} src={gif.preview} w="100%" />
				</Box>
			))}
		</VStack>
	);
}
